import React, { useState, useEffect } from 'react';
import { MessageCircle, Menu, X, ArrowLeft } from 'lucide-react';
import { STUDIO } from '../data/studio';

/**
 * The bar sits transparently over the dark hero until you scroll, then becomes
 * an opaque light bar. Those are two different backgrounds, so the type has to
 * be two different colours — it was espresso in both states, which made the
 * brand and the links almost invisible over the hero.
 *
 * Nav items are derived from what is actually mounted: a link to a section that
 * did not render scrolls nowhere and reads as a broken page.
 *
 * No admin entry point here by design. The studio reaches it at /admin.
 */
export default function Navbar({ activeView, setActiveView, onOpenQuote, showTransformations }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setMobileMenuOpen(false);
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleWhatsAppContact = () => {
    const msg = encodeURIComponent(
      'Hello Handmade bee, I would like to ask about placing an order.'
    );
    window.open(`https://wa.me/${STUDIO.phone}?text=${msg}`, '_blank', 'noopener');
  };

  const inAdmin = activeView === 'admin';
  // Over the hero the ground is dark; once the bar solidifies it is warm white.
  const onDark = !scrolled && !inAdmin;

  const links = [
    { href: '#showcase', label: 'Portfolio' },
    ...(showTransformations ? [{ href: '#transformations', label: 'Before & after' }] : []),
    { href: '#questions', label: 'Questions' },
  ];

  const goHome = () => {
    setActiveView('showcase');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || inAdmin
          ? 'bg-[#FFFBF7]/95 backdrop-blur-md border-b border-[#4E2C23]/12 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">

        <button
          onClick={goHome}
          className="flex items-center gap-3 group bg-transparent border-0 p-0 text-left"
        >
          {/* Not inverted on dark: the mark is fine line work with a filled
              floral, and brightness-0/invert flattens the peony into a white
              blob. A light chip keeps the real colours legible on both grounds. */}
          <span
            className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-105 ${
              onDark ? 'bg-[#FFF7F0]/95 shadow-sm' : 'bg-[#FFF7F0] border border-[#4E2C23]/10'
            }`}
          >
            {/* 96px, not 256. This renders into a 36px circle in the bar and
                sits above the fold — the 256px file was 52.8 KB of critical
                path for a mark nobody sees at that size. 96 still covers a
                3x display. AtelierStrip keeps the 256 because it draws it at
                128 CSS px, and it lazy-loads. */}
            <img
              src="/logo-mark-96.webp"
              alt=""
              aria-hidden="true"
              width="96"
              height="96"
              fetchPriority="high"
              className="w-9 h-9 object-contain"
            />
          </span>
          <span className="leading-none">
            <span
              className={`font-90s text-xl sm:text-[22px] font-black block tracking-tight transition-colors ${
                onDark ? 'text-[#FFF7F0]' : 'text-[#4E2C23]'
              }`}
            >
              {STUDIO.name}
            </span>
            <span
              className={`text-[10px] uppercase tracking-[0.2em] font-semibold block mt-1 transition-colors ${
                onDark ? 'text-[#FFDAB9]/80' : 'text-[#745147]'
              }`}
            >
              Handcrafted Atelier
            </span>
          </span>
        </button>

        {inAdmin ? (
          <button
            onClick={goHome}
            className="px-4 py-2.5 rounded-full text-xs font-bold bg-[#FFDAB9]/60 text-[#4E2C23] border border-[#4E2C23]/20 hover:bg-[#4E2C23] hover:text-[#FFF7F0] transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to site</span>
          </button>
        ) : (
          <>
            {/* Text-only links. An icon beside every nav item is decoration
                that repeats what the word already says. */}
            <div className="hidden md:flex items-center gap-8 text-[13px] font-semibold tracking-wide">
              {links.map(({ href, label }) => (
                <a
                  key={href}
                  href={href}
                  className={`py-2 border-b transition-colors ${
                    onDark
                      ? 'text-[#FFF7F0]/85 border-transparent hover:text-white hover:border-[#FFDAB9]'
                      : 'text-[#4E2C23] border-transparent hover:text-[#C75841] hover:border-[#E2725B]'
                  }`}
                >
                  {label}
                </a>
              ))}
            </div>

            {/* One button, not two competing pills. WhatsApp is a quiet
                secondary next to the action that matters. */}
            <div className="hidden md:flex items-center gap-5">
              <button
                onClick={handleWhatsAppContact}
                className={`text-[13px] font-semibold flex items-center gap-2 transition-colors bg-transparent border-0 p-0 ${
                  onDark ? 'text-[#FFDAB9] hover:text-white' : 'text-[#745147] hover:text-[#C75841]'
                }`}
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.9} />
                <span>WhatsApp</span>
              </button>

              <button
                onClick={onOpenQuote}
                className="btn-burnt-peach px-5 py-2.5 text-xs font-bold uppercase tracking-wider"
              >
                Start an Order
              </button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              className={`md:hidden p-2.5 rounded-xl border transition-colors ${
                onDark
                  ? 'text-[#FFF7F0] bg-white/10 border-white/25'
                  : 'text-[#4E2C23] bg-[#FFFBF7] border-[#4E2C23]/20'
              }`}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </>
        )}
      </div>

      {mobileMenuOpen && !inAdmin && (
        <div
          id="mobile-nav"
          className="md:hidden bg-[#FFFBF7] border-b border-[#4E2C23]/12 px-6 py-5 mt-3 space-y-1"
        >
          {links.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 text-sm font-semibold text-[#4E2C23] hover:text-[#C75841] border-b border-[#4E2C23]/8"
            >
              {label}
            </a>
          ))}

          <div className="pt-4 flex flex-col gap-2.5">
            <button
              onClick={() => { setMobileMenuOpen(false); onOpenQuote(); }}
              className="btn-burnt-peach w-full py-3 text-xs font-bold uppercase tracking-wider"
            >
              Start an Order
            </button>
            <button
              onClick={() => { setMobileMenuOpen(false); handleWhatsAppContact(); }}
              className="w-full py-3 text-xs font-semibold text-[#745147] hover:text-[#C75841] flex items-center justify-center gap-2 bg-transparent border-0"
            >
              <MessageCircle className="w-4 h-4" />
              Message on WhatsApp
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
